import { ExpressionValue, OneOfElements } from '@shared/models/project.model';
import {
  getElementQuantityRecord,
  isElementQuantityExpressionName,
} from '../../element-quantity.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';
import { isExpressionValue } from '@shared/helpers/expression-factory.helpers';

type ElementQuantityFallbackExpressionChange =
  ProjectChangeMap[ProjectChangeType.ElementQuantityFallbackExpression];

/**
 * Applies a formula change to {@link IElementQuantityExpressionProperty.fallbackCount}
 * when the quantity uses auto / fallback (no explicit {@link IElementQuantityExpressionProperty.count}).
 *
 * @param element - Current element state
 * @param change - Fallback expression payload
 * @returns Partial element update or undefined when not applicable / no-op
 */
export const elementQuantityFallbackExpressionToChange = (
  element: OneOfElements,
  change: ElementQuantityFallbackExpressionChange,
): Partial<OneOfElements> | undefined => {
  const quantityRecord = getElementQuantityRecord(element);
  const quantity = quantityRecord[change.quantityName];
  // Use name-based check to avoid getCount() throwing on legacy rows without count/fallbackCount.
  if (!quantity || !isElementQuantityExpressionName(quantity.name))
    return undefined;

  const existing = quantity.fallbackCount as ExpressionValue | undefined;

  if (
    isExpressionValue(existing) &&
    existing.expression === change.expression
  ) {
    return undefined;
  }

  return {
    quantity: {
      ...quantityRecord,
      [change.quantityName]: {
        ...quantity,
        // Bootstrap fallbackCount when missing (e.g. legacy rows without it).
        fallbackCount: { ...(existing ?? {}), expression: change.expression },
      },
    },
  };
};
